import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef, HostListener } from '@angular/core';
import * as moment from 'moment';

export interface CalendarDay {
    date: moment.Moment;
    dayOfMonth: number;
    isCurrentMonth: boolean;
    isToday: boolean;
}

export interface DurationPreset {
    label: string;
    getRange: (anchor: moment.Moment) => { start: moment.Moment; end: moment.Moment };
}

@Component({
    selector: 'vf-date-range-picker',
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['./date-range-picker.component.css']
})
export class DateRangePickerComponent implements OnInit, OnChanges, OnDestroy {

    @Input() startDate: moment.Moment | undefined;
    @Input() endDate: moment.Moment | undefined;
    @Input() placeholder: string = 'Set dates';
    @Input() todayDate: moment.Moment;
    /**
     * Which edge of the trigger the popover hangs from.
     *
     * ⚠️ NOT called `align` — a static `align="right"` at a call site lands on the host as a real
     * presentational attribute that Blink maps to `text-align`, inherited by everything inside with
     * no author rule to explain it. See the fuller note on `vf-date-picker`.
     */
    @Input() popoverAlign: 'left' | 'right' = 'right';
    @Input() variant: 'default' | 'toolbar' | 'field' | 'minimal' | 'row' = 'default';
    @Input() size: 'default' | 'sm' = 'default';
    @Input() disabled: boolean = false;

    @Output() dateRangeApplied = new EventEmitter<{ startDate: moment.Moment | undefined; endDate: moment.Moment | undefined }>();
    @Output() dateRangeCleared = new EventEmitter<void>();

    @ViewChild('triggerEl', { static: false }) triggerEl: ElementRef;
    @ViewChild('popoverEl', { static: false }) popoverEl: ElementRef;

    isOpen = false;

    workingStart: moment.Moment | undefined;
    workingEnd: moment.Moment | undefined;

    private snapshotStart: moment.Moment | undefined;
    private snapshotEnd: moment.Moment | undefined;

    selectionPhase: 'idle' | 'picking-end' = 'idle';
    activeField: 'start' | 'end' = 'start';
    hoverDate: moment.Moment | undefined;

    displayedMonth: number = 0;
    displayedYear: number = 0;
    calendarWeeks: CalendarDay[][] = [];

    weekdays: string[] = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    private openingEvent: MouseEvent | null = null;

    readonly presets: DurationPreset[] = [
        { label: 'Today', getRange: (t) => ({ start: t.clone(), end: t.clone() }) },
        { label: 'Tomorrow', getRange: (t) => ({ start: t.clone().add(1, 'day'), end: t.clone().add(1, 'day') }) },
        { label: '1 Week', getRange: (t) => ({ start: t.clone(), end: t.clone().add(6, 'days') }) },
        { label: '2 Weeks', getRange: (t) => ({ start: t.clone(), end: t.clone().add(13, 'days') }) },
        { label: '1 Month', getRange: (t) => ({ start: t.clone(), end: t.clone().add(1, 'month').subtract(1, 'day') }) },
        { label: '3 Months', getRange: (t) => ({ start: t.clone(), end: t.clone().add(3, 'months').subtract(1, 'day') }) },
        { label: '6 Months', getRange: (t) => ({ start: t.clone(), end: t.clone().add(6, 'months').subtract(1, 'day') }) },
        { label: '1 Year', getRange: (t) => ({ start: t.clone(), end: t.clone().add(1, 'year').subtract(1, 'day') }) }
    ];

    ngOnInit(): void {
        this.todayDate = this.ensureMoment(this.todayDate) || moment().startOf('day');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.todayDate) {
            this.todayDate = this.ensureMoment(this.todayDate) || moment().startOf('day');
        }
        if (!this.isOpen) {
            if (changes.startDate) {
                this.workingStart = this.ensureMoment(this.startDate);
            }
            if (changes.endDate) {
                this.workingEnd = this.ensureMoment(this.endDate);
            }
        }
    }

    private ensureMoment(val: any): moment.Moment | undefined {
        if (!val) {
            return undefined;
        }
        if (moment.isMoment(val)) {
            // Extract date-only to avoid timezone shift (e.g. UTC midnight → previous day locally)
            return moment(val.format('YYYY-MM-DD'), 'YYYY-MM-DD');
        }
        const m = moment(val);
        if (m.isValid()) {
            return moment(m.format('YYYY-MM-DD'), 'YYYY-MM-DD');
        }
        return undefined;
    }

    ngOnDestroy(): void {
        // cleanup if needed
    }

    // --- Popover open/close ---

    open(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }
        if (this.isOpen) {
            this.dismiss();
            return;
        }

        this.openingEvent = event;

        this.snapshotStart = this.ensureMoment(this.startDate);
        this.snapshotEnd = this.ensureMoment(this.endDate);
        this.workingStart = this.ensureMoment(this.startDate);
        this.workingEnd = this.ensureMoment(this.endDate);
        this.selectionPhase = 'idle';
        this.activeField = 'start';
        this.hoverDate = undefined;

        const navDate = this.workingStart || this.todayDate || moment();
        this.displayedMonth = navDate.month();
        this.displayedYear = navDate.year();
        this.buildCalendarWeeks();

        this.isOpen = true;
    }

    dismiss(): void {
        if (!this.isOpen) {
            return;
        }
        // If a start date was selected but no end date yet, emit the start date
        if (this.workingStart && !this.workingEnd && this.selectionPhase === 'picking-end') {
            this.dateRangeApplied.emit({
                startDate: this.workingStart.clone().utc(true),
                endDate: undefined
            });
        }
        this.isOpen = false;
        this.selectionPhase = 'idle';
        this.hoverDate = undefined;
    }

    apply(): void {
        if (!this.workingStart || !this.workingEnd) {
            return;
        }
        this.dateRangeApplied.emit({
            startDate: this.workingStart.clone().utc(true),
            endDate: this.workingEnd.clone().utc(true)
        });
        this.isOpen = false;
        this.selectionPhase = 'idle';
        this.hoverDate = undefined;
    }

    clearInline(event: MouseEvent): void {
        event.stopPropagation();
        this.clearWorking();
        this.dateRangeCleared.emit();
    }

    clearWorking(): void {
        this.workingStart = undefined;
        this.workingEnd = undefined;
        this.selectionPhase = 'idle';
        this.activeField = 'start';
        this.hoverDate = undefined;
        this.buildCalendarWeeks();
    }

    clearAndEmit(): void {
        this.clearWorking();
        this.dateRangeCleared.emit();
        this.isOpen = false;
    }

    // --- Header field clicks ---

    onHeaderStartClick(): void {
        this.activeField = 'start';
        this.selectionPhase = 'idle';
        this.hoverDate = undefined;
        if (this.workingStart) {
            this.displayedMonth = this.workingStart.month();
            this.displayedYear = this.workingStart.year();
            this.buildCalendarWeeks();
        }
    }

    onHeaderEndClick(): void {
        this.activeField = 'end';
        if (this.workingStart) {
            this.selectionPhase = 'picking-end';
        }
        this.hoverDate = undefined;
        if (this.workingEnd) {
            this.displayedMonth = this.workingEnd.month();
            this.displayedYear = this.workingEnd.year();
            this.buildCalendarWeeks();
        }
    }

    // --- Day interactions ---

    onDayClick(day: CalendarDay): void {
        if (!day.isCurrentMonth) {
            return;
        }
        const clicked = day.date.clone();

        if (this.selectionPhase === 'idle') {
            this.workingStart = clicked;
            this.workingEnd = undefined;
            this.selectionPhase = 'picking-end';
            this.activeField = 'end';
            // Emit start date immediately so parent reflects it
            this.dateRangeApplied.emit({
                startDate: this.workingStart.clone().utc(true),
                endDate: undefined
            });
        } else {
            // picking-end phase
            if (clicked.isBefore(this.workingStart, 'day')) {
                // auto-swap
                this.workingEnd = this.workingStart.clone();
                this.workingStart = clicked;
            } else {
                this.workingEnd = clicked;
            }
            this.selectionPhase = 'idle';
            this.hoverDate = undefined;
            this.buildCalendarWeeks();
            this.apply();
            return;
        }

        this.buildCalendarWeeks();
    }

    onDayHover(day: CalendarDay): void {
        if (this.selectionPhase === 'picking-end' && day.isCurrentMonth) {
            this.hoverDate = day.date.clone();
        }
    }

    onDayLeave(): void {
        if (this.selectionPhase === 'picking-end') {
            this.hoverDate = undefined;
        }
    }

    // --- Presets ---

    applyPreset(preset: DurationPreset): void {
        const anchor = this.todayDate || moment().startOf('day');
        const range = preset.getRange(anchor);
        this.workingStart = range.start;
        this.workingEnd = range.end;
        this.selectionPhase = 'idle';
        this.activeField = 'start';
        this.hoverDate = undefined;

        this.displayedMonth = this.workingStart.month();
        this.displayedYear = this.workingStart.year();
        this.buildCalendarWeeks();
        this.dateRangeApplied.emit({
            startDate: this.workingStart.clone().utc(true),
            endDate: this.workingEnd.clone().utc(true)
        });
        this.isOpen = false;
        this.hoverDate = undefined;
    }

    // --- Calendar navigation ---

    prevMonth(): void {
        this.displayedMonth--;
        if (this.displayedMonth < 0) {
            this.displayedMonth = 11;
            this.displayedYear--;
        }
        this.buildCalendarWeeks();
    }

    nextMonth(): void {
        this.displayedMonth++;
        if (this.displayedMonth > 11) {
            this.displayedMonth = 0;
            this.displayedYear++;
        }
        this.buildCalendarWeeks();
    }

    get monthYearLabel(): string {
        return moment({ year: this.displayedYear, month: this.displayedMonth }).format('MMMM YYYY');
    }

    // --- Calendar grid builder ---

    buildCalendarWeeks(): void {
        const firstOfMonth = moment({ year: this.displayedYear, month: this.displayedMonth, day: 1 });
        const startOfGrid = firstOfMonth.clone().startOf('week'); // Sunday

        const today = this.todayDate || moment().startOf('day');
        const weeks: CalendarDay[][] = [];

        let current = startOfGrid.clone();
        for (let w = 0; w < 6; w++) {
            const week: CalendarDay[] = [];
            for (let d = 0; d < 7; d++) {
                week.push({
                    date: current.clone(),
                    dayOfMonth: current.date(),
                    isCurrentMonth: current.month() === this.displayedMonth,
                    isToday: current.isSame(today, 'day')
                });
                current.add(1, 'day');
            }
            // Skip row if entirely outside the current month
            if (week.some(d => d.isCurrentMonth)) {
                weeks.push(week);
            }
        }

        this.calendarWeeks = weeks;
    }

    // --- Day CSS classes ---

    getDayClasses(day: CalendarDay): { [key: string]: boolean } {
        if (!day.isCurrentMonth) {
            return { 'vf-drp-day--outside': true };
        }

        const isStart = !!(this.workingStart && day.date.isSame(this.workingStart, 'day'));
        const isEnd = !!(this.workingEnd && day.date.isSame(this.workingEnd, 'day'));
        const isSelected = isStart || isEnd;

        // Confirmed range (between start and end)
        let isInRange = false;
        if (this.workingStart && this.workingEnd) {
            isInRange = day.date.isAfter(this.workingStart, 'day') && day.date.isBefore(this.workingEnd, 'day');
        }

        // Hover preview range
        let isHoverRange = false;
        let hStart: moment.Moment | undefined;
        let hEnd: moment.Moment | undefined;
        if (this.selectionPhase === 'picking-end' && this.workingStart && this.hoverDate && !this.hoverDate.isSame(this.workingStart, 'day')) {
            hStart = this.hoverDate.isBefore(this.workingStart, 'day') ? this.hoverDate : this.workingStart;
            hEnd = this.hoverDate.isBefore(this.workingStart, 'day') ? this.workingStart : this.hoverDate;
            isHoverRange = day.date.isAfter(hStart, 'day') && day.date.isBefore(hEnd, 'day');
        }

        // Half-background: confirmed range or hover preview
        const confirmedRange = !!(this.workingStart && this.workingEnd && !this.workingStart.isSame(this.workingEnd, 'day'));
        const hasHoverRange = !!(hStart && hEnd);

        const isRangeStart = (isStart && confirmedRange) || (hasHoverRange && day.date.isSame(hStart, 'day'));
        const isRangeEnd = (isEnd && confirmedRange) || (hasHoverRange && day.date.isSame(hEnd, 'day'));

        return {
            'vf-drp-day--today': day.isToday && !isSelected && !isRangeStart && !isRangeEnd,
            'vf-drp-day--selected': isSelected,
            'vf-drp-day--in-range': (isInRange || isHoverRange) && !isSelected,
            'vf-drp-day--range-start': isRangeStart,
            'vf-drp-day--range-end': isRangeEnd
        };
    }

    // --- Duration badge ---

    get durationDays(): number | null {
        if (this.workingStart && this.workingEnd) {
            return this.workingEnd.diff(this.workingStart, 'days') + 1;
        }
        return null;
    }

    // --- Trigger display ---

    get triggerText(): string {
        const s = this.ensureMoment(this.startDate);
        const e = this.ensureMoment(this.endDate);

        if (!s && !e) {
            return '';
        }
        if (s && !e) {
            return s.format('MMM D') + ' ...';
        }
        if (!s && e) {
            return '... ' + e.format('MMM D');
        }
        if (s && e && s.isSame(e, 'day')) {
            return s.format('MMM D');
        }
        return s.format('MMM D') + ' - ' + e.format('MMM D');
    }

    get hasDates(): boolean {
        return !!(this.startDate || this.endDate);
    }

    get startDateText(): string {
        const s = this.ensureMoment(this.startDate);
        return s ? s.format('MMM D') : '';
    }

    get endDateText(): string {
        const e = this.ensureMoment(this.endDate);
        return e ? e.format('MMM D') : '';
    }

    // --- Click outside / Escape ---

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.isOpen) {
            return;
        }
        // Skip the exact same event that opened the popover
        if (this.openingEvent && event === this.openingEvent) {
            this.openingEvent = null;
            return;
        }
        this.openingEvent = null;

        const target = event.target as HTMLElement;
        if (this.triggerEl && this.triggerEl.nativeElement.contains(target)) {
            return;
        }
        if (this.popoverEl && this.popoverEl.nativeElement.contains(target)) {
            return;
        }
        this.dismiss();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.isOpen) {
            this.dismiss();
        }
    }

    // --- Helpers ---

    isSameDay(a: moment.Moment | undefined, b: moment.Moment | undefined): boolean {
        if (!a || !b) {
            return false;
        }
        return a.isSame(b, 'day');
    }
}
